import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { rememberTool } from '../utils/lastTool';
import { useSubscription } from '../context/SubscriptionContext';
import { useConsent } from '../context';
import { useUser } from '@clerk/clerk-react';
// 144 px of logo, not the 1024 px master: it is drawn 36 px high, and the big
// one cost 155 kB on the first load of every page
import logoImage from '../assets/images/logo-144.png';
import { useLocale } from '../i18n/LocaleContext';
import { intlTag } from '../i18n/locales';
import { pathFor } from '../i18n/routes';

// Import shadcn/ui components
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Import icons from Lucide React
import {
  Menu,
  Plus,
  Scissors,
  FileText,
  Clock,
  Captions,
  Sparkles,
  ChevronDown,
  Film,
  Wand2,
  Tag,
  User,
  Shield,
  PenTool,
  CalendarClock,
  Users,
  LogOut,
} from "lucide-react";
import { useUsageStore, selectUsageState } from "../stores/usageStore";
import ThemeToggle from './ThemeToggle';
import { useSidebar } from './AppShell/SidebarContext';

function Navigation() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pulseUsage, setPulseUsage] = useState(false);
  const [lastFetch, setLastFetch] = useState(0);
  const prevMetricsRef = useRef();
  
  const { user, isAuthenticated, logout } = useAuth();
  const { isProPlan, subscription } = useSubscription();
  const { openPreferences } = useConsent();
  const { isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { t, locale } = useLocale();
  // With the app's sidebar on a wide screen, the bar keeps the logo and Create: the tool
  // switcher, the usage chip, the theme and the account live in the sidebar
  const sidebar = useSidebar();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // The tool a signed-in person is using, kept for their next sign-in (utils/lastTool.js)
  useEffect(() => {
    if (isAuthenticated) rememberTool(location.pathname);
  }, [isAuthenticated, location.pathname]);

  // Usage store (Zustand)
  const usageStore = useUsageStore();
  const { metrics, loading } = useUsageStore(selectUsageState);

  useEffect(() => {
    if (metrics && prevMetricsRef.current) {
      const currentMinutes = metrics.video_processing_minutes?.used || 0;
      const prevMinutes = prevMetricsRef.current.video_processing_minutes?.used || 0;
      const currentProjects = metrics.ai_agent_projects?.used || 0;
      const prevProjects = prevMetricsRef.current.ai_agent_projects?.used || 0;

      if (currentMinutes > prevMinutes || currentProjects > prevProjects) {
        setPulseUsage(true);
        const timer = setTimeout(() => setPulseUsage(false), 1500); // Animation duration
        return () => clearTimeout(timer);
      }
    }
    prevMetricsRef.current = metrics;
  }, [metrics]);

  useEffect(() => {
    if (!isAuthenticated) {
      usageStore.reset();
      return;
    }
    usageStore.fetchUsage();
    const id = usageStore.startPolling(60000);
    return () => usageStore.stopPolling(id);
  }, [isAuthenticated]);

  const handleLogout = useCallback(() => logout(), [logout]);
  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  // Each links to the page in the reader's language where one exists
  const featureLinks = [
    { id: 'aiClipping', href: pathFor('aiClipping', locale), Icon: Scissors },
    { id: 'autoCaption', href: pathFor('autoCaption', locale), Icon: Captions },
    { id: 'reframe', href: pathFor('videoReframe', locale), Icon: Scissors },
    { id: 'aiAgent', href: pathFor('aiAgent', locale), Icon: Sparkles },
    { id: 'editor', href: pathFor('editorFeature', locale), Icon: PenTool },
  ].map((link) => ({ ...link, label: t(`featureLinks.${link.id}.label`), description: t(`featureLinks.${link.id}.description`) }));
  // The Spanish and Portuguese readers came for the AI Agent: it leads their list
  if (locale !== 'en') featureLinks.unshift(...featureLinks.splice(3, 1));

  // The platform's tools, shown inline in the bar for signed-in users so
  // every feature stays one click away wherever they are (this replaces the
  // old second nav row). Order matches the journey; `match` marks the active
  // tool, including its project detail pages.
  const tools = [
    { label: t('tools.aiClipping'), href: '/ai-clipping', Icon: Scissors, match: /^\/ai-clipping/ },
    { label: t('tools.captions'), href: '/auto-caption', Icon: Captions, match: /^\/auto-caption/ },
    { label: t('tools.reframe'), href: '/video-reframe', Icon: Film, match: /^\/video-reframe/ },
    { label: t('tools.editor'), href: '/editor', Icon: PenTool, badge: t('nav.badgeBeta'), match: /^\/editor/ },
    { label: t('tools.aiAgent'), href: '/ai-agent', Icon: Sparkles, badge: t('nav.badgeNew'), match: /^\/ai-agent/ },
  ];

  // Compact usage values for navbar chip
  const usage = metrics?.video_processing_minutes;
  const aiAgentUsage = metrics?.ai_agent_projects;
  const percentage = Math.min(100, usage?.percentage || 0);
  const usedLabel = typeof usage?.used === 'number' ? usage.used.toFixed(1) : null;
  const limitLabel = usage?.limit === 'Unlimited' ? '∞' : usage?.limit;
  // On the AI Agent's pages the chip leads with what limits you there, videos
  // left, at every width: on a phone it used to show only minutes
  const agentFirst = /^\/ai-agent/.test(location.pathname) && aiAgentUsage && aiAgentUsage.limit !== 'Unlimited';
  const agentPercentage = agentFirst ? Math.min(100, (100 * aiAgentUsage.used) / Math.max(1, aiAgentUsage.limit)) : 0;
  const planName = subscription?.plan?.name || (isProPlan ? 'Pro' : 'Free');

  // Trial countdown calculation
  const isTrialing = subscription?.status === 'trialing';
  const trialEndDate = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  const daysRemaining = trialEndDate
    ? Math.ceil((trialEndDate - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  // Create action items for the Create dropdown
  const createActions = [
    { id: 'aiClipping', href: '/ai-clipping', Icon: Scissors },
    { id: 'autoCaption', href: '/auto-caption', Icon: Captions },
    { id: 'reframe', href: '/video-reframe', Icon: Film },
    { id: 'aiAgent', href: '/ai-agent', Icon: Sparkles },
    { id: 'series', href: '/ai-agent/series', Icon: CalendarClock },
    { id: 'characters', href: '/ai-agent/characters', Icon: Users },
    { id: 'editor', href: '/editor', Icon: PenTool },
  ].map((action) => ({ ...action, label: t(`createActions.${action.id}.label`), description: t(`createActions.${action.id}.description`) }));

  return (
    <header className="fixed top-0 start-0 end-0 h-16 border-b bg-background/95 backdrop-blur-lg z-50 shadow-sm">
      {/* Three tracks: logo | tools | actions. The side tracks share slack
          equally so the tools sit dead center, and each is at least its own
          content width, so nothing can ever overlap. */}
      <div className="container grid h-full grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 md:px-6 lg:gap-4">
        {/* Left Section: Logo */}
        <div className="col-start-1 flex items-center">
          <RouterLink to={pathFor('home', locale)} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src={logoImage} alt="Clip-Short Logo" width={144} height={144} className="site-logo h-8 w-auto md:h-9 drop-shadow-sm" />
            <span className="hidden sm:inline text-lg md:text-xl font-bold tracking-tight">Clip-Short</span>
          </RouterLink>
        </div>

        {/* Center Section: the tool switcher (signed in) or marketing links,
            centered between the equal-width logo and actions groups */}
        {isAuthenticated ? (
          <nav aria-label={t('nav.tools')} className={`col-start-2 hidden items-center gap-1 rounded-full border bg-muted/40 p-1 ${sidebar.shown ? '' : 'xl:flex'}`}>
            {tools.map(({ label, href, Icon, badge, match }) => {
              const active = match.test(location.pathname);
              return (
                <RouterLink
                  key={href}
                  to={href}
                  aria-current={active ? 'page' : undefined}
                  className={`relative flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 xl:px-3.5 py-1.5 text-[13px] transition-colors ${
 active
 ? 'bg-background font-semibold text-foreground shadow-sm'
 : 'font-medium text-muted-foreground hover:text-foreground'
 }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                  {badge && (
                    <span className="text-[9px] font-bold uppercase leading-none text-brand ">
                      {badge}
                    </span>
                  )}
                </RouterLink>
              );
            })}
          </nav>
        ) : (
          <div className="col-start-2 hidden lg:flex items-center justify-center gap-3">
            {/* Features Dropdown (marketing) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="font-medium gap-1">
                  {t('nav.features')}
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-2 rounded-xl shadow-xl" align="start">
                <DropdownMenuGroup className="space-y-1">
                  {featureLinks.map(({ label, description, href, Icon }) => (
                    <DropdownMenuItem
                      key={href}
                      asChild
                      className="group rounded-lg px-3 py-3.5 hover:bg-muted/50 focus:bg-muted/50 transition-colors cursor-pointer min-h-[52px]"
                    >
                      <RouterLink to={href} className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary/20 transition-colors shrink-0">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col items-start flex-1">
                          <span className="font-semibold text-sm">{label}</span>
                          <span className="text-xs text-muted-foreground line-clamp-1">{description}</span>
                        </div>
                      </RouterLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" asChild className={isActive(pathFor('pricing', locale)) ? 'text-primary font-semibold' : 'font-medium'}>
              <RouterLink to={pathFor('pricing', locale)}>{t('nav.pricing')}</RouterLink>
            </Button>
          </div>
        )}

        {/* Right Section: usage, actions, menus */}
        <div className="col-start-3 flex items-center justify-end gap-2 lg:gap-3">
        {/* Compact usage indicator - Always visible */}
        {isAuthenticated && !sidebar.shown && (
          (loading && !metrics) ? (
            <div className="flex items-center gap-2 sm:gap-3 rounded-full border bg-muted px-2 sm:px-2.5 py-1 shrink-0 w-[140px] sm:w-[200px] h-[32px]">
              {/* Video minutes skeleton */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-12 sm:w-16 h-1.5 bg-muted-foreground/20 rounded-full animate-pulse" />
                <div className="w-10 sm:w-12 h-3 bg-muted-foreground/20 rounded animate-pulse" />
              </div>
              {/* Divider */}
              <div className="h-4 w-px bg-border" />
              {/* AI Agent skeleton */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="h-3 w-3 bg-muted-foreground/20 rounded animate-pulse" />
                <div className="w-8 h-3 bg-muted-foreground/20 rounded animate-pulse" />
              </div>
            </div>
          ) : usage ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`flex items-center gap-2 sm:gap-3 rounded-full border bg-background/60 px-2 sm:px-2.5 py-1 cursor-default transition-all shrink-0 ${
 pulseUsage ? 'animate-pulse-once' : ''
 }`}
                  >
                    {agentFirst ? (
                      /* AI Agent videos, on the AI Agent's pages */
                      <div className="flex items-center gap-1.5 sm:gap-2" data-testid="usage-chip-agent">
                        <Sparkles className="h-3 w-3 text-muted-foreground" />
                        <div className="w-12 sm:w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full ${agentPercentage >= 90 ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${agentPercentage}%` }} />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{aiAgentUsage.used}/{aiAgentUsage.limit}</span>
                      </div>
                    ) : (
                    /* Video Minutes */
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="w-12 sm:w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${percentage >= 90 ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${percentage}%` }} />
                      </div>
                      {usedLabel != null && (
                        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{usedLabel}/{limitLabel}</span>
                      )}
                    </div>
                    )}

                    {/* AI Agent Projects — the bar gets tight next to the tool
                        switcher, so this second metric waits for a wide screen */}
                    {!agentFirst && aiAgentUsage && aiAgentUsage.limit !== "Unlimited" && (
                      <div className="hidden 2xl:flex items-center gap-1.5 sm:gap-2">
                        <div className="h-4 w-px bg-border" />
                        <Sparkles className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{aiAgentUsage.used}/{aiAgentUsage.limit}</span>
                      </div>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-2 p-1">
                    <p className="text-sm">
                      {limitLabel === '∞'
                        ? t('nav.minutesUnlimited')
                        : t('nav.minutesLeft', { n: Math.max(0, (usage.limit - usage.used).toFixed(1)) })}
                    </p>
                    {aiAgentUsage && aiAgentUsage.limit !== "Unlimited" && (
                      <p className="text-sm">
                        {t('nav.agentLeft', { n: Math.max(0, aiAgentUsage.limit - aiAgentUsage.used) })}
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null
        )}

        {/* Right Section: Actions and User Menu */}
        <div className="hidden sm:flex items-center justify-end gap-2 lg:gap-3 shrink-0">
          {!sidebar.shown && <ThemeToggle />}
          {/* Trial countdown indicator - compact, hidden on md, shown on lg */}
          {isAuthenticated && isTrialing && daysRemaining > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="hidden lg:flex gap-1 px-2 py-0.5 bg-gradient-to-r from-indigo-500/10 to-indigo-50 dark:to-indigo-950 border-indigo-500/30 ">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs font-medium">{t('nav.trialShort', { n: daysRemaining })}</span>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">
                    {t('nav.trialEnds', { date: trialEndDate?.toLocaleDateString(intlTag(locale), { month: 'short', day: 'numeric' }) })}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Create Button - Primary CTA */}
          {isAuthenticated && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1.5 shadow-sm">
                  <Wand2 className="h-4 w-4" />
                  {t('nav.create')}
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-72 p-2 rounded-xl shadow-xl"
                align="end"
              >
                <DropdownMenuGroup className="space-y-1">
                  {createActions.map(({ label, description, href, Icon }) => (
                    <DropdownMenuItem
                      key={href}
                      asChild
                      className="group rounded-lg px-3 py-3 hover:bg-muted/50 focus:bg-muted/50 transition-colors cursor-pointer min-h-[48px]"
                    >
                      <RouterLink to={href} className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary/20 transition-colors shrink-0">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col items-start flex-1">
                          <span className="font-semibold text-sm">{label}</span>
                          <span className="text-xs text-muted-foreground">{description}</span>
                        </div>
                      </RouterLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* User Menu (in the sidebar on a wide screen) */}
          {isAuthenticated && sidebar.shown ? null : isAuthenticated ? (
            <DropdownMenu onOpenChange={(open) => {
              if (open) {
                const now = Date.now();
                // Only refetch if last fetch was > 5 seconds ago
                if (now - lastFetch > 5000) {
                  usageStore.fetchUsage();
                  setLastFetch(now);
                }
              }
            }}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu" className={`rounded-full p-0 hover:opacity-80 transition-opacity ${isProPlan ? 'ring-2 ring-primary/50' : ''}`}>
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.email?.charAt(0) || 'U')}`} alt={user?.full_name || t('nav.user')} />
                    <AvatarFallback>{user?.email?.charAt(0).toUpperCase() || 'U'}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rounded-xl shadow-xl">
                {/* User info */}
                <div className="px-3 py-3 border-b">
                  <p className="font-semibold text-sm">{user?.full_name || t('nav.user')}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
                  {isProPlan && (
                    <Badge className="mt-2 bg-primary/10 text-primary hover:bg-primary/10 text-[10px] uppercase font-bold px-2 border-none">Pro</Badge>
                  )}
                </div>

                {/* Usage metrics: the last ones known stay up while they are asked for
                    again (every time the menu opens, and every minute); placeholders
                    only before the first answer */}
                {(loading && !metrics) ? (
                  <div className="p-3 border-b space-y-2">
                    <div className="h-3 w-16 rounded bg-muted animate-pulse"></div>
                    <div className="space-y-1">
                      <div className="h-2 w-full rounded bg-muted animate-pulse"></div>
                      <div className="h-1.5 w-full rounded-full bg-muted animate-pulse"></div>
                    </div>
                  </div>
                ) : metrics && (
                  <div className="p-3 border-b">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">{t('nav.videoMinutes')}</span>
                      <span className="text-xs font-bold">
                        {metrics.video_processing_minutes?.used?.toFixed(1)} / {metrics.video_processing_minutes?.limit === 'Unlimited' ? '∞' : metrics.video_processing_minutes?.limit}
                      </span>
                    </div>
                    {metrics.video_processing_minutes?.limit !== 'Unlimited' && (
                      <Progress
                        value={metrics.video_processing_minutes?.percentage || 0}
                        className={`h-1.5 ${metrics.video_processing_minutes?.percentage >= 90 ? '[&>div]:bg-destructive' : ''}`}
                      />
                    )}
                    {!isProPlan && (
                      <Button variant="outline" size="sm" asChild className="w-full mt-2.5 text-xs font-semibold h-7">
                        <RouterLink to={pathFor('pricing', locale)}>{t('nav.upgrade')}</RouterLink>
                      </Button>
                    )}
                  </div>
                )}

                {/* Menu items */}
                <div className="py-1">
                  <DropdownMenuItem asChild><RouterLink to="/account">{t('nav.myAccount')}</RouterLink></DropdownMenuItem>
                  {user?.is_admin && (<DropdownMenuItem asChild><RouterLink to="/admin">{t('nav.admin')}</RouterLink></DropdownMenuItem>)}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            clerkLoaded && !isSignedIn && (
              <div className="flex gap-2">
                <Button variant="outline" asChild size="sm">
                  <RouterLink to="/login">{t('nav.login')}</RouterLink>
                </Button>
                <Button asChild size="sm">
                  <RouterLink to="/signup">{t('nav.getStarted')}</RouterLink>
                </Button>
              </div>
            )
          )}
        </div>
        
        {/* Mobile Navigation */}
        <div className="flex sm:hidden items-center gap-2 ms-auto">
          {/* Create button on mobile - larger touch target */}
          {isAuthenticated && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="default" className="gap-1.5 h-10 px-3">
                  <Wand2 className="h-4 w-4" />
                  <span className="hidden xs:inline">{t('nav.create')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72 p-2 rounded-xl" align="end">
                <DropdownMenuGroup className="space-y-1">
                  {createActions.map(({ label, description, href, Icon }) => (
                    <DropdownMenuItem
                      key={href}
                      asChild
                      className="group rounded-lg px-3 py-3 cursor-pointer min-h-[44px]"
                    >
                      <RouterLink to={href} className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col items-start flex-1">
                          <span className="font-semibold text-sm">{label}</span>
                          <span className="text-xs text-muted-foreground">{description}</span>
                        </div>
                      </RouterLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mobile Menu Sheet */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              {isAuthenticated ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('nav.menu')}
                  className={`rounded-full p-0 ${
 isProPlan ? "ring-2 ring-primary/50" : ""
 }`}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={
                        user?.profile_picture_url ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          user?.email?.charAt(0) || "U"
                        )}`
                      }
                    />
                    <AvatarFallback>
                      {user?.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              ) : (
                <Button variant="ghost" size="icon" aria-label={t('nav.menu')}>
                  <Menu className="h-5 w-5" />
                </Button>
              )}
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full max-w-sm flex flex-col p-0"
            >
              <SheetHeader className="p-6 border-b">
                <SheetTitle className="text-start">
                  {isAuthenticated ? (
                    <div className="space-y-1">
                      <p className="font-semibold text-base">
                        {user?.full_name || t('nav.user')}
                      </p>
                      <p className="text-xs text-muted-foreground font-normal truncate">
                        {user?.email || ""}
                      </p>
                      {isProPlan && (
                        <Badge className="bg-primary/10 text-primary hover:bg-primary/10 text-[10px] uppercase font-bold px-2 border-none">
                          Pro
                        </Badge>
                      )}
                    </div>
                  ) : (
                    t('nav.menu')
                  )}
                </SheetTitle>
              </SheetHeader>

              <div className="overflow-y-auto p-6 space-y-8">
                {/* Usage section */}
                {isAuthenticated && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-4">
                      {t('nav.usage')}
                    </h3>
                    {(loading && !metrics) ? (
                      <div className="space-y-2">
                        <div className="h-2 w-full rounded bg-muted animate-pulse"></div>
                        <div className="h-1.5 w-full rounded-full bg-muted animate-pulse"></div>
                      </div>
                    ) : (
                      metrics && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-muted-foreground">
                              {t('nav.videoMinutes')}
                            </span>
                            <span className="text-xs font-bold">
                              {metrics.video_processing_minutes?.used?.toFixed(
                                1
                              )}{" "}
                              /{" "}
                              {metrics.video_processing_minutes?.limit ===
                              "Unlimited"
                                ? "∞"
                                : metrics.video_processing_minutes?.limit}
                            </span>
                          </div>
                          {metrics.video_processing_minutes?.limit !==
                            "Unlimited" && (
                            <Progress
                              value={
                                metrics.video_processing_minutes?.percentage ||
                                0
                              }
                              className={`h-1.5 ${
 metrics.video_processing_minutes?.percentage >=
 90
 ? "[&>div]:bg-destructive"
 : ""
 }`}
                            />
                          )}

                          {/* AI Agent Projects Usage */}
                          {aiAgentUsage && aiAgentUsage.limit !== "Unlimited" && (
                            <div className="mt-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-muted-foreground">
                                  {t('nav.aiAgentVideos')}
                                </span>
                                <span className="text-xs font-bold">
                                  {aiAgentUsage.used} / {aiAgentUsage.limit}
                                </span>
                              </div>
                              <Progress
                                value={(aiAgentUsage.used / aiAgentUsage.limit) * 100}
                                className={`h-1.5 ${
 (aiAgentUsage.used / aiAgentUsage.limit) * 100 >= 90
 ? "[&>div]:bg-destructive"
 : ""
 }`}
                              />
                            </div>
                          )}

                          {!isProPlan && (
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                              className="w-full mt-3 text-xs font-semibold"
                            >
                              <RouterLink to={pathFor('pricing', locale)} onClick={() => setIsMobileMenuOpen(false)}>
                                {t('nav.upgrade')}
                              </RouterLink>
                            </Button>
                          )}
                          {isTrialing && daysRemaining > 0 && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-brand ">
                              <Clock className="h-3 w-3" />
                              <span>
                                {t(daysRemaining === 1 ? 'nav.trialLeftOne' : 'nav.trialLeftMany', { n: daysRemaining })}
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                )}

                {/* Create / Features section */}
                <div className="space-y-1">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-3">
                    {isAuthenticated ? t('nav.create') : t('nav.features')}
                  </h3>
                  {(isAuthenticated ? createActions : featureLinks).map(
                    ({ label, href, Icon }) => (
                      <RouterLink
                        key={href}
                        to={href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted -mx-2 min-h-[52px]"
                      >
                        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <Icon className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span className="font-medium text-sm">{label}</span>
                      </RouterLink>
                    )
                  )}
                </div>

                {/* Menu Links */}
                <div className="space-y-1">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-3">
                    {t('nav.menu')}
                  </h3>
                  <div className="flex items-center justify-between gap-4 p-3 -mx-2 min-h-[52px]">
                    <span className="font-medium text-sm">{t('nav.theme')}</span>
                    <ThemeToggle compact />
                  </div>
                  <RouterLink
                    to={pathFor('pricing', locale)}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted -mx-2 min-h-[52px]"
                  >
                    <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Tag className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <span className="font-medium text-sm">{t('nav.pricing')}</span>
                  </RouterLink>
                  <RouterLink
                    to="/editor"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted -mx-2 min-h-[52px]"
                  >
                    <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <PenTool className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{t('tools.editor')}</span>
                      <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px] font-bold leading-none uppercase">
                        {t('nav.badgeBeta')}
                      </Badge>
                    </div>
                  </RouterLink>
                  {isAuthenticated && (
                    <>
                      <RouterLink
                        to="/account"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted -mx-2 min-h-[52px]"
                      >
                        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span className="font-medium text-sm">{t('nav.myAccount')}</span>
                      </RouterLink>
                      {user?.is_admin && (
                        <RouterLink
                          to="/admin"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted -mx-2 min-h-[52px]"
                        >
                          <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center shrink-0">
                            <Shield className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <span className="font-medium text-sm">
                            {t('nav.admin')}
                          </span>
                        </RouterLink>
                      )}
                      {/* Signing out is a quiet row with the account, not the one button pinned
                          under the thumb: it was, and "Cerrar sesión" reads as "close" to many.
                          On 2026-09-29 a new user signed out 17 s after arriving, twice, and
                          another at the moment their export was ready and needed three ways
                          of signing in to get back to it */}
                      <button
                        type="button"
                        onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
                        data-testid="mobile-logout"
                        className="flex w-full items-center gap-4 p-3 rounded-lg hover:bg-muted -mx-2 min-h-[52px] text-start text-muted-foreground"
                      >
                        <div className="h-11 w-11 flex items-center justify-center shrink-0">
                          <LogOut className="h-5 w-5" />
                        </div>
                        <span className="text-sm">{t('nav.logout')}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {!isAuthenticated && (
              <div className="p-6 mt-auto border-t bg-background">
                  <div className="space-y-2">
                    <Button asChild className="w-full">
                      <RouterLink to="/signup" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.getStartedFree')}</RouterLink>
                    </Button>
                    <Button variant="outline" asChild className="w-full">
                      <RouterLink to="/login" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.login')}</RouterLink>
                    </Button>
                  </div>
              </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
        </div>
      </div>
    </header>
  );
}

export default Navigation; 